import validate from 'validate.js';
import { get, isEmpty } from 'lodash';
import postalCodes from 'postal-codes-js';
import { addressFormErrors, getCountryLabelAndZones } from './billingAddressErrors';

const validateBookingRequestForm = (values, props) => {
  const {
    tags,
    number_of_guests,
    alcohol_consumed,
    addOns = [],
    are_host_rules_accepted,
    identity_verification,
    cardholders_name,
    booking_use_message,
    guest_social_events_policy_accepted,
    billing_country,
    billing_street,
    billing_city,
    billing_state,
    billing_zip,
    split_payment
  } = values;

  const { socialEventPolicyAgreementRequired, globalCountries } = props;

  const validateAddOns = addOn => {
    const count = get(addOn, 'info.count', null);
    const maxCount = get(addOn, 'info.max_count') || 999;
    // don't want to validate against maxCount of 0
    const validateMaxCount = maxCount > 0 ? maxCount : null;

    return {
      info: {
        count: validate.single(
          count,
          {
            numericality: {
              lessThanOrEqualTo: validateMaxCount,
              notLessThanOrEqualTo: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.max_quantity', defaultMessage: 'The max quantity is {count}' }, { count: maxCount })
            }
          }
        )
      }
    };
  };

  const countryLabelAndZones = getCountryLabelAndZones(globalCountries, billing_country);

  const { selectCountry, enterStreetAddress, enterCity, selectState, enterValidPostalCode } = addressFormErrors;
  const { labels = {}, zones = [] } = countryLabelAndZones;

  const selectedCountryHasZones = !isEmpty(zones);

  validate.validators.isValidZip = (value) => {
    const zipCheck = postalCodes.validate(billing_country, value);
    return true === zipCheck
      ? undefined
      : enterValidPostalCode[labels.postalCode || 'Postal code'];
  };

  return {
    tags: validate.single(get(tags, 'activity', null), {
      presence: {
        allowEmpty: false,
        message: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.enter_activity', defaultMessage: 'Please enter an activity.' })
      }
    }),
    number_of_guests: validate.single(number_of_guests, {
      presence: {
        allowEmpty: false,
        message: props.intl.formatMessage({ id: 'inbox.validate_booking_request_form.enter_attendees', defaultMessage: 'Please enter the number of attendees.' })
      },
      numericality: {
        greaterThan: 0,
        lessThanOrEqualTo: 100000,
        message: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.attendees_min_max', defaultMessage: 'The number of attendees must be between {min} and {max}.' }, { min: props.intl.formatNumber(1), max: props.intl.formatNumber(100000) })
      }
    }),
    alcohol_consumed: validate.single(alcohol_consumed, {
      presence: {
        allowEmpty: false,
        message: props.intl.formatMessage({ id: 'inbox.validate_booking_request_form.alcohol_option', defaultMessage: 'Please select an option.' })
      }
    }),

    addOns: addOns.map(validateAddOns),
    booking_use_message: validate.single(booking_use_message, {
      presence: {
        allowEmpty: false,
        message: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.enter_message', defaultMessage: 'Please enter a message.' })
      },
      length: {
        minimum: 6,
        tooShort: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.min_characters', defaultMessage: 'Minimum is {count} characters.' }, { count: props.intl.formatNumber(6) }),
        maximum: 10000,
        tooLong: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.max_characters', defaultMessage: 'Maximum is {count} characters.' }, { count: props.intl.formatNumber(10000) })
      }
    }),
    identity_verification: validate.single(identity_verification, {
      presence: {
        message: 'Please verify your identity.'
      },
      inclusion: {
        within: ['verified'],
        message: 'Please verify your identity.'
      }
    }),
    cardholders_name: validate.single(cardholders_name, {
      presence: {
        allowEmpty: false,
        message: props.intl.formatMessage({ id: 'inbox.validate_booking_request_form.enter_name', defaultMessage: "Enter cardholder's name and last name."})
      }
    }),
    are_host_rules_accepted: validate.single(are_host_rules_accepted, {
      presence: {
        message: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.accept_rules', defaultMessage: 'Please accept the rules.' })
      },
      inclusion: {
        within: [true],
        message: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.accept_rules_inclusion', defaultMessage: 'Please accept the rules.' })
      }
    }),
    billing_country: validate.single(billing_country, {
      presence: {
        allowEmpty: false,
        message: selectCountry
      }
    }),
    billing_street: validate.single(billing_street, {
      presence: {
        allowEmpty: false,
        message: enterStreetAddress
      }
    }),
    billing_city: validate.single(billing_city, {
      presence: {
        allowEmpty: false,
        message: enterCity[labels.city || 'City']
      }
    }),
    // only validate if the selected country has provinces
    billing_state: validate.single(billing_state, {
      presence: {
        allowEmpty: !selectedCountryHasZones,
        message: selectState[labels.zone || 'State']
      }
    }),
    billing_zip: validate.single(billing_zip, {
      isValidZip: true
    }),
    // only validate guest_social_events_policy_accepted if the field is required
    // we can add this conditional bc the socialEventPolicyAgreementRequired prop can't change in the request flow
    guest_social_events_policy_accepted: socialEventPolicyAgreementRequired ? validate.single(guest_social_events_policy_accepted, {
      presence: {
        message: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.accept_social_policy', defaultMessage: 'Please accept the Social Event Policy.' })
      },
      inclusion: {
        within: [true],
        message: props.intl.formatMessage({ id: 'bkc.validate_booking_request_form.accept_social_policy_inclusion', defaultMessage: 'Please accept the Social Event Policy.' })
      }
    }) : null
  };
};

export default validateBookingRequestForm;
